import { useEffect, useState } from 'react';
import { FileText, X } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';

/**
 * VISOR DE ARCHIVO EN GRANDE — ventana emergente centrada, sobre la misma pantalla.
 *
 * Nombre para futuras referencias: **FileViewerModal**. Antes cada módulo metía un <iframe>
 * dentro de su tarjeta y el PDF quedaba diminuto (el COC en la cola era ilegible). Esto lo abre
 * al 90% de la pantalla sin sacar al usuario de donde está: se cierra con la ✕, con Escape o
 * clicando afuera.
 *
 * Se le pasa `fileId` (lo resuelve contra /api/files/:id/url) o directamente una `url`.
 */
/** ¿Es un video? Por el mime si viene; si no, por la extensión del nombre (la lista de Files no trae mime). */
export function esVideo(mime: string | null | undefined, name: string | null | undefined): boolean {
  if (mime && /^video\//i.test(mime)) return true;
  return /\.(mp4|m4v|webm|mov|ogv)$/i.test(name ?? '');
}

export function FileViewerModal({ fileId, url, title, onClose, actions, mime }: {
  fileId?: string | null;
  url?: string | null;
  title?: string | null;
  onClose: () => void;
  /** Botones extra arriba a la derecha (ej. "Anexar al email" desde el composer, 2026-08-10). */
  actions?: React.ReactNode;
  /** Record (spec §4.12.5): los `video/*` (doc_type 'recording') se abren con <video>, no en iframe. */
  mime?: string | null;
}) {
  const { t } = useI18n();
  const [src, setSrc] = useState<string | null>(url ?? null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (url) { setSrc(url); return; }
    if (!fileId) return;
    let alive = true;
    api.get<{ url: string }>(`/api/files/${fileId}/url`)
      .then((r) => { if (alive) setSrc(r.url); })
      .catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, [fileId, url]);

  // Escape cierra (es una ventana emergente, no una página).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose} role="dialog" aria-modal="true"
      style={{ position: 'fixed', inset: 0, zIndex: 4000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
    >
      <div
        className="card"
        style={{ width: '90vw', height: '90vh', maxWidth: 1200, padding: 0, display: 'flex', flexDirection: 'column' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: '1px solid var(--line)' }}>
          <b style={{ flex: 1, fontSize: 'var(--fs-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <FileText size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {title || t('fv.file')}
          </b>
          {actions}
          {src && <a className="btn ghost sm" href={src} target="_blank" rel="noreferrer">↗ {t('fv.openTab')}</a>}
          <button className="calx-pop-x" onClick={onClose}><X size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /></button>
        </div>
        <div style={{ flex: 1, minHeight: 0, background: '#fff' }}>
          {err ? (
            <div className="muted" style={{ padding: 16, fontSize: 'var(--fs-sm)' }}>{t('fv.error')}</div>
          ) : !src ? (
            <div className="muted" style={{ padding: 16, fontSize: 'var(--fs-sm)' }}>{t('common.loadingDots')}</div>
          ) : esVideo(mime, title) ? (
            <video controls playsInline preload="metadata" src={src} title={title || 'video'} style={{ width: '100%', height: '100%', background: '#000', display: 'block' }} />
          ) : (
            <iframe title={title || 'file'} src={src} style={{ width: '100%', height: '100%', border: 0 }} />
          )}
        </div>
      </div>
    </div>
  );
}
